import { memo } from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import Markdown from 'react-markdown';
import styles from './style/SegmentPersona.module.css';

const DEFAULT_PLACEHOLDER = `Paste or type your persona details here...

Example:

- Voice: Professional, friendly, and supportive
- Behaviors: Responds with empathy, provides actionable advice
- Data: Use customer purchase history and preferences`;

type PersonaReadOnlyProps = {
  /** The persona text to display (renders as markdown) */
  persona: string | null | undefined;
  /** Placeholder text to show when persona is empty (renders as markdown) */
  placeholder?: string;
  /** Callback when edit button is clicked */
  onEdit: () => void;
  /** Accessible label for the edit button */
  editButtonAriaLabel?: string;
  /** Whether whitelabel theming is enabled (optional) */
  whitelabelEnabled?: boolean;
  /** Primary color for whitelabel theming (optional) */
  primaryColor?: string;
};

/**
 * PersonaReadOnly displays persona text as rendered markdown with an edit button.
 * This is a purely presentational component with no state management.
 */
const PersonaReadOnlyComponent = ({
  persona,
  placeholder = DEFAULT_PLACEHOLDER,
  onEdit,
  editButtonAriaLabel = 'Edit persona',
  whitelabelEnabled,
  primaryColor,
}: PersonaReadOnlyProps) => {
  // Build inline styles for whitelabel theming
  const iconButtonStyle: React.CSSProperties = whitelabelEnabled && primaryColor
    ? {
      '--wl-icon-color': primaryColor,
    } as React.CSSProperties
    : {};

  return (
    <div className={styles['persona-display-container']}>
      <div className={styles['persona-content']}>
        {persona ? (
          <Markdown>{persona}</Markdown>
        ) : (
          <div className={styles.placeholder}>
            <Markdown>{placeholder}</Markdown>
          </div>
        )}
      </div>
      <div
        className={styles['icon-button']}
        style={iconButtonStyle}
        onClick={onEdit}
        role="button"
        tabIndex={0}
        aria-label={editButtonAriaLabel}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onEdit(); }}
      >
        <EditIcon sx={{ fontSize: 18 }} />
      </div>
    </div>
  );
};

export const PersonaReadOnly = memo(PersonaReadOnlyComponent);
